import React, { useState, useEffect, useRef } from 'react';
import './ImageCarousel.css';

const ImageCarousel = ({ images }) => {
  const [scrollPosition, setScrollPosition] = useState(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isHoveringLeft, setIsHoveringLeft] = useState(false);
  const [isHoveringRight, setIsHoveringRight] = useState(false);
  const [isJumping, setIsJumping] = useState(false);
  const [isScrolling, setIsScrolling] = useState(false);
  const animationFrameRef = useRef(null);
  const containerRef = useRef(null);
  const trackRef = useRef(null);
  const isJumpingRef = useRef(false);
  const velocityRef = useRef(0);
  const lastTimeRef = useRef(null);

  // Duplicate images for infinite scroll effect
  const allImages = [...images, ...images, ...images];
  
  // Calculate image width (367.5px + 30px gap = 397.5px)
  const imageWidth = 397.5;
  const totalWidth = images.length * imageWidth;

  // Initialize scroll position to middle set
  useEffect(() => {
    setScrollPosition(-totalWidth);
  }, [totalWidth]);

  // Smooth animation using requestAnimationFrame
  useEffect(() => {
    const animate = (currentTime) => {
      if (lastTimeRef.current === null) {
        lastTimeRef.current = currentTime;
        animationFrameRef.current = requestAnimationFrame(animate);
        return;
      }
      
      const deltaTime = currentTime - lastTimeRef.current;
      lastTimeRef.current = currentTime;

      if (isHoveringLeft || isHoveringRight) {
        // Accelerate when hovering starts
        const targetVelocity = isHoveringLeft ? 0.15 : -0.15; // pixels per millisecond
        const acceleration = 0.003; // acceleration rate
        
        if (Math.abs(velocityRef.current) < Math.abs(targetVelocity)) {
          velocityRef.current += acceleration * (targetVelocity > 0 ? 1 : -1);
        } else {
          velocityRef.current = targetVelocity;
        }
        
        setIsScrolling(true);
      } else {
        // Decelerate smoothly when hovering stops
        if (Math.abs(velocityRef.current) > 0.01) {
          velocityRef.current *= 0.95; // exponential decay
          setIsScrolling(true);
        } else {
          velocityRef.current = 0;
          setIsScrolling(false);
          lastTimeRef.current = null;
          animationFrameRef.current = null;
          return;
        }
      }

      // Update position
      setScrollPosition((prev) => prev + velocityRef.current * deltaTime);
      
      // Continue animation
      animationFrameRef.current = requestAnimationFrame(animate);
    };

    // Start animation if hovering or if still decelerating
    if (isHoveringLeft || isHoveringRight || Math.abs(velocityRef.current) > 0.01) {
      if (animationFrameRef.current === null) {
        lastTimeRef.current = null;
        animationFrameRef.current = requestAnimationFrame(animate);
      }
    }

    return () => {
      if (animationFrameRef.current !== null) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, [isHoveringLeft, isHoveringRight]);

  // Reset scroll position for seamless infinite loop
  useEffect(() => {
    if (isJumpingRef.current) return;
    
    // When scrolling left (position goes negative beyond first set)
    if (scrollPosition <= -totalWidth * 2) {
      isJumpingRef.current = true;
      setIsJumping(true);
      setScrollPosition(prev => prev + totalWidth);
      setTimeout(() => {
        isJumpingRef.current = false;
        setIsJumping(false);
      }, 50);
    } 
    // When scrolling right (position goes positive beyond last set)
    else if (scrollPosition >= 0) {
      isJumpingRef.current = true;
      setIsJumping(true);
      setScrollPosition(prev => prev - totalWidth);
      setTimeout(() => {
        isJumpingRef.current = false;
        setIsJumping(false);
      }, 50);
    }
  }, [scrollPosition, totalWidth]);

  const openModal = (image) => {
    setSelectedImage(image);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedImage(null);
  };

  return (
    <>
      <div className="carousel-wrapper">
        <div className="carousel-divider"></div>
        <div className="carousel-container" ref={containerRef}>
          <div 
            className="carousel-arrow carousel-arrow-left"
            onMouseEnter={() => setIsHoveringLeft(true)}
            onMouseLeave={() => setIsHoveringLeft(false)}
          >
            ‹
          </div>
          
          <div className="carousel-track-container">
            <div 
              ref={trackRef}
              className="carousel-track"
              style={{ 
                transform: `translateX(${scrollPosition}px)`,
                transition: isJumping ? 'none' : (isScrolling ? 'none' : 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)')
              }}
            >
              {allImages.map((image, index) => (
                <div 
                  key={index}
                  className="carousel-image-wrapper"
                  onClick={() => openModal(image)}
                >
                  <img src={image} alt={`Carousel ${index}`} className="carousel-image" />
                </div>
              ))}
            </div>
          </div>
          
          <div 
            className="carousel-arrow carousel-arrow-right"
            onMouseEnter={() => setIsHoveringRight(true)}
            onMouseLeave={() => setIsHoveringRight(false)}
          >
            ›
          </div>
        </div>
      </div>

      {isModalOpen && (
        <div className="carousel-modal" onClick={closeModal}>
          <div className="carousel-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="carousel-modal-close" onClick={closeModal}>×</button>
            <img src={selectedImage} alt="Full size" className="carousel-modal-image" />
          </div>
        </div>
      )}
    </>
  );
};

export default ImageCarousel;

